import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../config/axiosConfig';
import {
    Container, Typography, CircularProgress, Alert, Box, Button,
    FormControl, InputLabel, Input, InputAdornment, useTheme, alpha
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import { trackEvent } from '../utils/analytics';
import { trackRedditConversion } from '../utils/redditPixel';

const EmailVerification = () => {
    const theme = useTheme();
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [verificationFailed, setVerificationFailed] = useState(false);
    const [resendEmail, setResendEmail] = useState('');
    const [resendMessage, setResendMessage] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const verifyEmail = async () => {
            try {
                const queryParams = new URLSearchParams(location.search);
                const success = queryParams.get('success');

                if (success === 'true') {
                    setMessage('Email verified successfully. You can now log in.');
                    setLoading(false);
                    trackEvent('user_engagement', 'sign_up_complete', 'email');
                    trackRedditConversion('SignUp', { method: 'email' });
                    return;
                }

                if (success === 'false') {
                    setMessage(queryParams.get('message') || 'Email verification failed.');
                    setVerificationFailed(true);
                    setLoading(false);
                    return;
                }

                const token = queryParams.get('token');

                if (!token) {
                    setMessage('Invalid or missing token');
                    setVerificationFailed(true);
                    setLoading(false);
                    return;
                }

                const response = await axios.get(`/verify-email?token=${token}`, {
                    withCredentials: true
                });

                if (response.data.success) {
                    setMessage('Email verified successfully. You can now log in.');
                } else {
                    setMessage('Email verification failed. The token may be invalid or expired.');
                    setVerificationFailed(true);
                }
            } catch (error) {
                console.error('Error during email verification:', error);
                setMessage('An error occurred during email verification. Please try again later.');
                setVerificationFailed(true);
            } finally {
                setLoading(false);
            }
        };

        verifyEmail();
    }, [location.search]);

    const handleResendVerification = async () => {
        if (!resendEmail.trim()) {
            setResendMessage('Please enter your email address');
            return;
        }
        setResendLoading(true);
        setResendMessage('');
        try {
            const response = await axios.post('/resend-verification', { email: resendEmail });
            setResendMessage(response.data.message);
        } catch (err) {
            if (err.response?.status === 429) {
                setResendMessage('Too many requests. Please try again later.');
            } else {
                setResendMessage(err.response?.data?.message || 'Something went wrong.');
            }
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <Container sx={{ mt: 6, mb: 15 }}>
            <Box textAlign="center">
                {loading ? (
                    <CircularProgress />
                ) : (
                    <>
                        <Alert severity={message.includes('successfully') ? 'success' : 'error'}>
                            {message}
                        </Alert>

                        {message.includes('successfully') && (
                            <Box sx={{ mt: 2 }}>
                                <Button variant="contained" onClick={() => navigate('/Pricing')}>
                                    Start Here
                                </Button>
                            </Box>
                        )}

                        {verificationFailed && (
                            <Box sx={{ mt: 4, maxWidth: 400, mx: 'auto' }}>
                                <Typography variant="body1" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                                    Need a new verification email? Enter your email below.
                                </Typography>

                                <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                                    <InputLabel htmlFor="resend-email">Email address</InputLabel>
                                    <Input
                                        id="resend-email"
                                        type="email"
                                        value={resendEmail}
                                        onChange={(e) => setResendEmail(e.target.value)}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <EmailIcon sx={{ color: alpha(theme.palette.primary.main, 0.7) }} />
                                            </InputAdornment>
                                        }
                                    />
                                </FormControl>

                                <Button
                                    variant="contained"
                                    onClick={handleResendVerification}
                                    disabled={resendLoading}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderRadius: 1.5,
                                    }}
                                >
                                    {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                                </Button>

                                {resendMessage && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        {resendMessage}
                                    </Alert>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Container>
    );
};

export default EmailVerification;